import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  FaArrowRight,
  FaChartLine,
  FaShieldAlt,
  FaRocket,
  FaBolt,
  FaCoins,
  FaLayerGroup,
} from "react-icons/fa";
import About from "./About";
import Blog from "./Blog";
import Brandname from "./Brandname";
import Card from "./Card";
import Contact from "./Contact";
import Counting from "./Counting";
import Features from "./Features";
import Ourvision from "./Ourvision";
import SEO from "../../../utils/Seo";

/**
 * NOTE ON FONTS
 * The hero pairs a display serif with real character ("Fraunces" — it has
 * genuinely curled, hand-drawn letterforms at display sizes) against a clean
 * grotesk body face ("Manrope"). For best performance, add this to
 * public/index.html <head> instead of relying on the runtime @import below:
 *
 * <link rel="preconnect" href="https://fonts.googleapis.com">
 * <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
 * <link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,600;1,9..144,500&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet">
 */
const HERO_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,600;1,9..144,500&family=Manrope:wght@400;500;600;700;800&display=swap');

  .hero-font-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .hero-font-body { font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif; }

  @keyframes hero-dash { to { stroke-dashoffset: -32; } }
  @keyframes hero-border-flow { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
  @keyframes hero-drift-a { 0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); } 50% { transform: translate(2%, -4%) rotate(8deg) scale(1.06); } }
  @keyframes hero-drift-b { 0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); } 50% { transform: translate(-3%, 3%) rotate(-6deg) scale(1.04); } }
  @keyframes hero-drift-c { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(1%, 2%) scale(1.08); } }
  @keyframes hero-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(6px); } }

  .motion-safe-drift-a { animation: hero-drift-a 16s ease-in-out infinite; }
  .motion-safe-drift-b { animation: hero-drift-b 19s ease-in-out infinite; }
  .motion-safe-drift-c { animation: hero-drift-c 13s ease-in-out infinite; }
  .motion-safe-border-flow { background-size: 200% 100%; animation: hero-border-flow 5s linear infinite; }
  .motion-safe-bob { animation: hero-bob 2.2s ease-in-out infinite; }
  .motion-safe-dash-line { animation: hero-dash 3s linear infinite; }

  @media (prefers-reduced-motion: reduce) {
    .motion-safe-drift-a, .motion-safe-drift-b, .motion-safe-drift-c,
    .motion-safe-border-flow, .motion-safe-bob, .motion-safe-dash-line { animation: none !important; }
  }
`;

// Reusable hand-drawn blob outline — used for background wash, small accents,
// and as the panel's clip-path anchor. Not a perfect circle on purpose.
const BLOB_D =
  "M70,8 C102,2 128,24 138,54 C148,84 132,112 102,124 C72,136 34,128 20,98 C6,68 26,26 70,8 Z";

// Live-feeling revenue ticker: counts up on mount, then idles with a small
// believable jitter — the way a real analytics widget looks without a data
// connection behind it.
const useLiveCounter = (target, prefersReducedMotion) => {
  const [value, setValue] = useState(prefersReducedMotion ? target : 0);

  useEffect(() => {
    if (prefersReducedMotion) {
      setValue(target);
      return undefined;
    }
    let raf;
    const duration = 1400;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const jitter = setInterval(() => {
      setValue(target + Math.round(Math.sin(Date.now() / 900) * 37));
    }, 2600);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(jitter);
    };
  }, [target, prefersReducedMotion]);

  return value;
};

const Sparkle = ({ className, delay = 0, prefersReducedMotion }) => (
  <motion.span
    aria-hidden="true"
    className={`absolute w-1.5 h-1.5 rounded-full bg-[#D97706] ${className}`}
    initial={{ opacity: 0.25, y: 0 }}
    animate={
      prefersReducedMotion
        ? { opacity: 0.5 }
        : { opacity: [0.25, 0.9, 0.25], y: [0, -10, 0] }
    }
    transition={{ duration: 3.4, repeat: prefersReducedMotion ? 0 : Infinity, delay }}
  />
);

const Home = () => {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const revenue = useLiveCounter(18420, prefersReducedMotion);

  return (
    <>
      <SEO
        title="MyAd360 - Monetize Your Website with Google AdX & Header Bidding"
        description="Boost your website revenue using MyAd360's AI-powered ad monetization tools including Google AdX, header bidding, and programmatic ads."
        ogTitle="Monetize Your Website with MyAd360"
        ogDescription="Maximize your ad revenue with AI-driven ad monetization platform."
        keywords="Ad optimization, ad monetization, Google AdX partner, header bidding, website ad revenue, ad management, MyAd360"
      />
      <style>{HERO_STYLES}</style>

      <div className="w-full bg-slate-50/50 overflow-hidden">
        {/* ================= HERO ================= */}
        <section
          id="homeID"
          className="hero-font-body relative overflow-hidden pt-16 sm:pt-20 lg:pt-24 pb-16 sm:pb-20"
          style={{
            background:
              "radial-gradient(120% 90% at 15% 0%, #FEF3E5 0%, transparent 55%), radial-gradient(110% 90% at 100% 10%, #FFF3E0 0%, transparent 50%), radial-gradient(100% 80% at 50% 100%, #FFF4E5 0%, transparent 55%), #FFFBF5",
          }}
        >
          {/* ---- ambient blob layer ---- */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
            <svg
              className="absolute -top-16 -left-20 w-[30rem] h-[30rem] opacity-[0.55] blur-2xl motion-safe-drift-a"
              viewBox="0 0 150 140"
            >
              <path d={BLOB_D} fill="url(#gradA)" />
              <defs>
                <linearGradient id="gradA" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FDDBB8" />
                  <stop offset="100%" stopColor="#FDDCB5" />
                </linearGradient>
              </defs>
            </svg>
            <svg
              className="absolute top-10 -right-24 w-[26rem] h-[26rem] opacity-[0.5] blur-2xl motion-safe-drift-b"
              viewBox="0 0 150 140"
            >
              <path d={BLOB_D} fill="url(#gradB)" />
              <defs>
                <linearGradient id="gradB" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%" stopColor="#FDDCB5" />
                  <stop offset="100%" stopColor="#FDDBB8" />
                </linearGradient>
              </defs>
            </svg>
            <svg
              className="absolute bottom-[-8rem] left-1/3 w-[24rem] h-[24rem] opacity-[0.45] blur-2xl motion-safe-drift-c"
              viewBox="0 0 150 140"
            >
              <path d={BLOB_D} fill="url(#gradC)" />
              <defs>
                <linearGradient id="gradC" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FDDCB5" />
                  <stop offset="100%" stopColor="#FDDCB5" />
                </linearGradient>
              </defs>
            </svg>

          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative">
            {/* ---- headline zone ---- */}
            <div className="max-w-3xl mx-auto text-center space-y-7 relative">
              <Sparkle className="top-2 left-4" delay={0} prefersReducedMotion={prefersReducedMotion} />
              <Sparkle className="top-10 right-8" delay={0.8} prefersReducedMotion={prefersReducedMotion} />
              <Sparkle className="-bottom-2 left-1/4" delay={1.6} prefersReducedMotion={prefersReducedMotion} />

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="flex items-center w-full gap-4"
              >
                <span
                  aria-hidden="true"
                  className="hidden sm:inline-block h-[2px] flex-1"
                  style={{
                    background: "linear-gradient(to left, #EA580C, transparent)",
                  }}
                />
                <span className="text-xs sm:text-sm font-bold tracking-wide text-[#92400E] whitespace-nowrap">
                  Next-Gen Programmatic Monetization
                </span>
                <span
                  aria-hidden="true"
                  className="hidden sm:inline-block h-[2px] flex-1"
                  style={{
                    background: "linear-gradient(to right, #EA580C, transparent)",
                  }}
                />
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="hero-font-display text-[2.1rem] sm:text-5xl lg:text-[3.8rem] leading-[1.12] sm:leading-[1.08] text-[#1C1410] px-1"
                style={{ fontWeight: 600, letterSpacing: "-0.015em" }}
              >
                Supercharge your site's revenue with{" "}
                <span className="relative inline whitespace-normal sm:whitespace-nowrap sm:inline-block">
                  <span
                    className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C] underline decoration-[#EA580C] decoration-2 underline-offset-[6px] sm:no-underline"
                    style={{ fontStyle: "italic", fontWeight: 500 }}
                  >
                    advanced ad serving
                  </span>
                  <svg
                    className="hidden sm:block absolute left-0 -bottom-1.5 w-full"
                    height="10"
                    viewBox="0 0 260 10"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2,7 C50,1 90,10 130,5 C170,0 210,9 258,4"
                      fill="none"
                      stroke="url(#underlineGrad)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <defs>
                      <linearGradient id="underlineGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#D97706" />
                        <stop offset="100%" stopColor="#EA580C" />
                      </linearGradient>
                    </defs>
                  </svg>
                </span>{" "}
                & demand partners.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.22 }}
                className="text-base sm:text-lg text-[#4D3E30] leading-relaxed max-w-xl mx-auto"
              >
                Our platform empowers publishers globally to earn more from
                their current ad setup by tapping into top-tier ad exchanges,
                header bidding, and smart monetization tools.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.34 }}
                className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4"
              >
                <button
                  onClick={() => navigate("/signup")}
                  className="group inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#D97706] to-[#EA580C] hover:from-[#EA580C] hover:to-[#D97706] text-white font-extrabold px-8 py-3.5 rounded-full shadow-[0_10px_25px_-5px_rgba(217,119,6,0.45)] hover:shadow-[0_14px_30px_-5px_rgba(217,119,6,0.6)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 text-base"
                >
                  <span>Get Started Free</span>
                  <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </button>

                <a
                  href="#servicesID"
                  className="p-[1.5px] rounded-full motion-safe-border-flow"
                  style={{
                    backgroundImage:
                      "linear-gradient(90deg, #D97706, #EA580C, #D97706, #D97706)",
                  }}
                >
                  <span className="flex items-center justify-center gap-2 bg-[#FFFBF5] hover:bg-white text-[#2F3B34] font-bold px-7 py-[0.9rem] rounded-full transition-colors duration-300 text-base">
                    See how it works
                  </span>
                </a>
              </motion.div>
            </div>

            {/* ---- live auction showcase ---- */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4, ease: "easeOut" }}
              className="relative mt-16 lg:mt-20 max-w-4xl mx-auto"
            >
              {/* floating badge, top-left */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.0 }}
                className="hidden sm:flex absolute -top-6 left-4 sm:left-10 items-center gap-2 bg-[#1C1410] text-white text-[11px] font-bold px-3.5 py-2 shadow-xl -rotate-2 z-10"
                style={{ borderRadius: "1.4rem 0.6rem 1.4rem 0.6rem" }}
              >
                <span className="w-2 h-2 rounded-full bg-[#FBD38D] motion-safe:animate-pulse" />
                900M+ impressions / day
              </motion.div>

              {/* floating badge, bottom-right */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.15 }}
                className="hidden sm:flex absolute -bottom-6 right-4 sm:right-10 items-center gap-2 bg-white text-[#92400E] text-[11px] font-bold px-3.5 py-2 shadow-xl border border-[#FDE8CD] rotate-2 z-10"
                style={{ borderRadius: "0.6rem 1.4rem 0.6rem 1.4rem" }}
              >
                <FaCoins className="text-[#D97706]" />
                +142% RPM boost
              </motion.div>

              <div
                className="relative bg-white/70 backdrop-blur-xl border border-white shadow-[0_40px_80px_-30px_rgba(22,34,27,0.35)] p-6 sm:p-8"
                style={{ borderRadius: "3rem 1.5rem 3rem 1.5rem" }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 items-center">
                  {/* demand sources */}
                  <div className="space-y-3">
                    <p className="text-[11px] font-bold text-[#8A8168] uppercase tracking-wide mb-2">
                      Demand competing
                    </p>
                    {[
                      { icon: <FaLayerGroup />, name: "Header Bidding" },
                      { icon: <FaBolt />, name: "Google AdX" },
                      { icon: <FaShieldAlt />, name: "PMP Deals" },
                    ].map((src, i) => (
                      <motion.div
                        key={src.name}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.6 + i * 0.1 }}
                        className="flex items-center gap-2.5 text-sm font-semibold text-[#3D2E22] bg-[#FFF4E5] border border-[#F5DFC5] px-3.5 py-2.5"
                        style={{ borderRadius: "1.2rem 0.6rem 1.2rem 0.6rem" }}
                      >
                        <span className="text-[#C47E1A]">{src.icon}</span>
                        {src.name}
                      </motion.div>
                    ))}
                  </div>

                  {/* connecting ribbon */}
                  <div className="hidden lg:block w-28">
                    <svg viewBox="0 0 120 160" className="w-full h-40" aria-hidden="true">
                      <path
                        d="M6,20 C60,10 40,80 70,80 C100,80 60,140 114,140"
                        fill="none"
                        stroke="#F0DCC8"
                        strokeWidth="3"
                      />
                      <path
                        d="M6,20 C60,10 40,80 70,80 C100,80 60,140 114,140"
                        fill="none"
                        stroke="#C47E1A"
                        strokeWidth="3"
                        strokeDasharray="6 10"
                        strokeLinecap="round"
                        className="motion-safe-dash-line"
                      />
                      {!prefersReducedMotion &&
                        [0, 1, 2].map((i) => (
                          <circle key={i} r="4.5" fill="#D97706">
                            <animateMotion
                              dur="3.2s"
                              begin={`${i * 1.05}s`}
                              repeatCount="indefinite"
                              path="M6,20 C60,10 40,80 70,80 C100,80 60,140 114,140"
                            />
                          </circle>
                        ))}
                    </svg>
                  </div>
                  <div className="lg:hidden flex justify-center">
                    <svg viewBox="0 0 200 60" className="w-40 h-14" aria-hidden="true">
                      <path d="M6,30 C60,10 140,50 194,30" fill="none" stroke="#F0DCC8" strokeWidth="3" />
                      <path
                        d="M6,30 C60,10 140,50 194,30"
                        fill="none"
                        stroke="#C47E1A"
                        strokeWidth="3"
                        strokeDasharray="6 10"
                        strokeLinecap="round"
                        className="motion-safe-dash-line"
                      />
                    </svg>
                  </div>

                  {/* revenue readout */}
                  <div
                    className="text-center lg:text-right bg-gradient-to-br from-[#FFF4E5] to-[#FFF3E0] p-5"
                    style={{ borderRadius: "1.6rem 2.4rem 1.6rem 2.4rem" }}
                  >
                    <p className="text-[11px] font-bold text-[#8A8168] uppercase tracking-wide">
                      Today's yield
                    </p>
                    <p
                      className="hero-font-display text-4xl text-[#1C1410] tabular-nums"
                      style={{ fontWeight: 600 }}
                    >
                      ${revenue.toLocaleString()}
                    </p>
                    <span className="inline-flex items-center gap-1.5 mt-2 text-[#92400E] text-xs font-bold bg-white/70 px-2.5 py-1.5 rounded-full">
                      <FaChartLine />
                      Auctioned in &lt;80ms avg
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* ---- trust chips ---- */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-14 flex flex-wrap items-center justify-center gap-3"
            >
              {[
                { icon: <FaChartLine />, label: "+40% Revenue Growth" },
                { icon: <FaShieldAlt />, label: "100% Brand Safety" },
                { icon: <FaRocket />, label: "Google AdX Access" },
              ].map((item) => (
                <span
                  key={item.label}
                  className="inline-flex items-center gap-2 text-xs font-bold text-[#3D2E22] bg-white/80 backdrop-blur px-3.5 py-2.5 border border-[#F0DCC8] shadow-sm rounded-full"
                >
                  <span className="text-[#C47E1A] text-sm">{item.icon}</span>
                  {item.label}
                </span>
              ))}
            </motion.div>
          </div>

        </section>

        {/* Other Sections */}
        <Brandname />
        <Card />
        <Features />
        <About />
        <Ourvision />
        <Counting />
        <Blog />
        <Contact />
      </div>
    </>
  );
};

export default Home;